import { ChangeDetectorRef, Component, HostListener, Injector, ViewChild, ViewEncapsulation } from '@angular/core';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { DetailedMedicinalProductViewComponent } from '../../../shared/component/detailed-view/detailed-medicinal-product-view/detailed-medicinal-product-view.component';
import { TreeViewComponent } from '../../../shared/component/tree-view/tree-view.component';
import { AuthService } from '../../../shared/service/auth-service/auth-service.service';
import { Art57Section } from '../../../shared/ui/Art57Section/Art57Section';
import { Art57Node } from '../../../shared/ui/Node/Art57Node/Art57Node';
import { MINIMUM_TREE_WITDH } from '../../../shared/ui/Constants';
import { BaseNode } from '../../../shared/ui/Node/BaseNode';
import { SectionService } from '../../../shared/service/section-service/section-service';
import { QueryNode } from '../../../shared/ui/Node/QueryNode';
import { QueryFieldsNode } from '../../../shared/ui/Node/QueryFieldsNode';
import { Art57NodeUtils } from '../../../shared/ui/Node/Art57Node/Art57NodeUtils';
import { QueryConditionsNode } from '../../../shared/ui/Node/QueryConditionsNode';
import { MedicinalProductsQueryResultNode } from '../../../shared/ui/Node/MedicinalProductsNode/MedicinalProductsQueryResultNode';
import { AutoUploadNode } from '../../../shared/ui/Node/AutoUploadNode';
import { AutoUploadList } from '../../../shared/ui/Node/MedicinalProductsNode/ListEntity/AutoUploadList';
import { MedicinalproductsService } from '../../../shared/service/medicinalproducts-service/medicinalproducts.service';
import { AuthorisedProductsValidVersionQueryNode } from '../../../shared/ui/Node/MedicinalProductsNode/Query/AuthorisedProducts/AuthorisedProductsValidVersionQueryNode';
import { AuthorisedProductsLastVersionQueryNode } from '../../../shared/ui/Node/MedicinalProductsNode/Query/AuthorisedProducts/AuthorisedProductsLastVersionQueryNode';


@Component({
  selector: 'app-medicinal-product',
  templateUrl: './medicinal-product.component.html',
  styleUrl: './medicinal-product.component.scss',
  encapsulation: ViewEncapsulation.None
})

export class MedicinalProductComponent {

  @ViewChild('tree_view_component') tree_view_component!: TreeViewComponent;
  @ViewChild('detail_medicinal_product_view_component') detail_view_component!: DetailedMedicinalProductViewComponent;
  nodes: BaseNode[] = [];
  selectedNode: BaseNode | undefined;
  
  constructor(
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    public sectionService: SectionService,
    public injector: Injector) {
  }

  //Initialize 
  ngOnInit() {
    this.initializeSection();
    this.sectionService.changeMedicinalProductsSelectedNode$.subscribe({ next: node => this.onGoToSelectedNode(node) })

  }

  onResetApplication() {
    this.injector.get(Art57Section).resetApplication().then(applicationReseted => {
      if (applicationReseted) {
        this.initializeSection();
      }
    });
  }


  onResetSection() {
    this.injector.get(Art57Section).resetSection(this.injector.get(Art57Section).medicinalProduct).then(sectionReseted => {
      if (sectionReseted) {
        this.initializeSection();
      }
    });
  }


  leftColumnWidth: number = this.injector.get(Art57Section).tree_width;
  isTreeContainerVisible: boolean = true;
  isResizing: boolean = false;

  onResizing(isResizing: boolean): void {
    this.isResizing = isResizing;
  }

  onResize(clientX: number): void {
    const container = document.querySelector('#main-container') as HTMLElement;

    if (container) {
      const containerRect = container.getBoundingClientRect();
      this.injector.get(Art57Section).tree_width = clientX - containerRect.left;

      const minWidth = MINIMUM_TREE_WITDH; 
      const maxWidth = containerRect.width-100; 

      this.leftColumnWidth = Math.max(minWidth, Math.min(this.injector.get(Art57Section).tree_width, maxWidth));
    }
  }
  

  //Selection node event
  onNodeSelect(event: any) {

    this.injector.get(MedicinalproductsService).loading.next(false);

    const currentNode = event.node;
    if (currentNode instanceof AutoUploadNode){
      currentNode.loadEntity();
    }
    if (currentNode instanceof AutoUploadList) {
      currentNode.load();
    }
    
    if (event != null && event.node != null) {
      event.node.expanded = true;
      this.selectedNode = event.node;
    }
    
    if (this.selectedNode) {
      this.injector.get(Art57Section).medicinalProduct.selectedNode = this.selectedNode;
    }
  }

  //Model changed
  onModelChange(model: any) {

    if (this.selectedNode) {

      if (this.selectedNode instanceof Art57Node) {
        this.selectedNode.refreshLabel();
      }

    }

  }

  initializeSection() {
    //Create TreeView template

    this.nodes = this.injector.get(Art57Section).medicinalProduct.treeView;

    //    this.injector.get(Art57Section).productReports.getFieldsErrorsControl().resetFieldsFormControl(); // Reset error control 
    //Set selected node
    this.onNodeSelect({ node: this.injector.get(Art57Section).medicinalProduct.selectedNode });

  }

  expandParentNodes(node: TreeNode) {
    node.expanded = true;
    if (node.parent) {
      this.expandParentNodes(node.parent); // Recursively expand children
    }
  }

  moveTreeViewToNode(node: TreeNode) {
    this.expandParentNodes(node);
    node!.expanded = true;
    this.cdr.detectChanges(); // allow detection that component view is draw in browser so we can change the focus in the right view
    this.setTreeViewScroll(node.key!);
  }


  setTreeViewScroll(key: string) {
    if (key != '') {
      const treeView = this.tree_view_component;
      if (treeView) {
        treeView.setScrollAtSelectedElement(key);
      }
    }
  }

  onRemoveNode(nodeToRemove: any) {
    this.confirmationService.confirm({

      header: 'Remove',
      message: 'Are you sure you want to remove the results from the list?',
      icon: 'pi pi-question-circle',
      acceptLabel: 'OK',
      rejectLabel: 'Cancel',
      accept: () => {
        if (nodeToRemove && nodeToRemove.parent) {

          let parentNode: BaseNode = nodeToRemove.parent;
          parentNode!.children!.splice(parentNode!.children!.indexOf(nodeToRemove), 1);

          this.onGoToSelectedNode(parentNode);

          this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Item has been removed', life: environment.successElapsedTime });
        }
      },
      reject: () => {
        // do nothing
      }
    });
  }

  onRemoveSelectedNodes(nodeList: any) {

    if ((nodeList.children.filter((element: any) => element.checked)).length > 0) {
      this.confirmationService.confirm({

        header: 'Remove',
        message: 'Are you sure you want to remove all selected Items?',
        icon: 'pi pi-question-circle',
        acceptLabel: 'OK',
        rejectLabel: 'Cancel',
        accept: () => {
          if (nodeList) {
            nodeList.children = nodeList.children.filter((element: any) => !element.checked)
            this.selectedNode?.refreshLabel();
          }
          this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Items have been removed', life: environment.successElapsedTime });
        },
        reject: () => {
          // do nothing
        }
      });
    }
  }

  onButtonAction(buttonAction: any) {
    switch (buttonAction.action) {
      case 'resetApplication':
        this.onResetApplication();
        break;
      case 'resetSection':
        this.onResetSection();
        break;
      case 'remove':
        this.onRemoveNode(buttonAction.param);
        break;
      case 'removeall':
        this.onRemoveSelectedNodes(buttonAction.param);
        break;
      case 'runQuery':
        this.onRunQuery(buttonAction.param);
        break;
      case 'reRunQuery':
        this.onReRunQuery(buttonAction.param);
        break;
      case 'moveToModifyNode':
        this.onMoveToModifyNode(buttonAction.param);
        break;
      case 'exportToExcel':
        this.exportToExcel(buttonAction.param);
        break;
      case 'runToExcel':
        this.runToExcel(buttonAction.param);
        break;
      case 'nextPage':
        this.onNextPage(buttonAction.param);
        break;
      case 'topPage':
        this.onTopPage(buttonAction.param);
        break;
      case 'previousPage':
        this.onPreviousPage(buttonAction.param);
        break;
        case 'addPage':
        this.addPage(buttonAction.param);
        break;
    }
  }

  onRunQuery(selectedNode: QueryNode) {
      const queryNode:any = Art57NodeUtils.findFirstCurrentOrParentNodeOfType(selectedNode,QueryNode);
      queryNode.executeQuery(selectedNode, true);
  }

  onReRunQuery(selectedNode: QueryNode) {
      const queryNode:any = Art57NodeUtils.findFirstCurrentOrParentNodeOfType(selectedNode,QueryNode);
      queryNode.executeQuery(selectedNode, false);
  }

  onMoveToModifyNode(currentNode: any) {
    let goToNode  = Art57NodeUtils.findFirstChildNodeOfType(currentNode.parent.parent, QueryFieldsNode);
    const conditionsNode = Art57NodeUtils.findFirstChildNodeOfType(currentNode.parent.parent, QueryConditionsNode);    
    if (currentNode.conditionsNode!.data.fields.some((field: any) => field.props.isModified)) {
      const newNode = (currentNode.conditionsNode as QueryConditionsNode).cloneNode(goToNode!.parent as BaseNode);
      currentNode.parent.parent.children[1] = newNode;
      goToNode  = Art57NodeUtils.findFirstChildNodeOfType(newNode.parent, QueryConditionsNode);
    } else {
      const newNode = (currentNode.fieldsNode as QueryFieldsNode).cloneNode(goToNode!.parent as BaseNode);
      currentNode.parent.parent.children[0] = newNode;
      goToNode  = Art57NodeUtils.findFirstChildNodeOfType(newNode.parent, QueryFieldsNode);
    }
    this.onNodeSelect({ node: goToNode });
  }
  
  exportToExcel(selectedNode: MedicinalProductsQueryResultNode) {
    selectedNode.exportToExcel();
  }

  runToExcel(selectedNode: BaseNode) {
    const queryNode: any = Art57NodeUtils.findFirstCurrentOrParentNodeOfType(selectedNode, QueryNode);
    if (this.checkNodeOwner(queryNode)) {
      queryNode.executeQueryToExcel();
    }
  }

  private checkNodeOwner(queryNode: any): boolean {
    const isEMA = this.injector.get(AuthService).isEMAOrganisation();
    
    if (isEMA) {
      return true;
    }
    
    if (queryNode instanceof AuthorisedProductsValidVersionQueryNode
      || queryNode instanceof AuthorisedProductsLastVersionQueryNode
    ) {
      const conditionNode = Art57NodeUtils.findFirstChildNodeOfType(queryNode, QueryConditionsNode);
      const base = ["art57", "ma_valid", "owned"];
      if (conditionNode != null &&
          (conditionNode.data.model['owned'] == null ||
            conditionNode.data.model['owned'] == undefined)) {
              this.confirmationService.confirm({
                header: 'Message from webpage',
                message: "Please indicate yes or no in the Owned Condition",
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'OK',
                rejectVisible: false,
                accept: () => {
                  //Do nothing
                },
              });
              return false;
            } else if (conditionNode != null && 
              conditionNode.data.model['owned'] == 0 &&
              !Object.keys(conditionNode.data.model).some(key => !base.includes(key) && (conditionNode.data.model[key] || conditionNode.data.model[key] == ""))) {
              this.confirmationService.confirm({
                header: 'Message from webpage',
                message: `It is not possible to run an advanced query on all authorised products in the XEVMPD.
                 You must specify at least one condition for your search in addition to those already specified by default`,
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'OK',
                rejectVisible: false,
                accept: () => {
                  //Do nothing
                },
              });
              return false;
            }
      }

    return true;
  }

  onNextPage(selectedNode: QueryNode) {
    selectedNode.nextPage(selectedNode);
  }

  onPreviousPage(selectedNode: QueryNode) {
    selectedNode.previousPage(selectedNode);
  }

  onTopPage(selectedNode: QueryNode) {
   selectedNode.topPage(selectedNode);
  }

  addPage(selectedNode: QueryNode) {
    selectedNode.addPage(selectedNode);
  }

  onGoToSelectedNode(selectedNode: BaseNode) {
    this.onNodeSelect({ node: selectedNode });
    this.moveTreeViewToNode(this.selectedNode as TreeNode);
    this.expandParentNodes(selectedNode);
  }

  toggleTreeContainer(): void {
    this.isTreeContainerVisible = !this.isTreeContainerVisible;
  }

}
